<template>
  <rh-etapa-card
    titulo="Revisão e Envio"
    subtitulo="Confira as informações antes de salvar"
    icone="mdi-check-circle-outline"
  >
    <v-alert v-if="!alterado" dense text type="info" class="mb-4">
      Nenhuma alteração pendente. O botão Salvar fica habilitado quando algo for alterado.
    </v-alert>

    <v-row>
      <v-col v-for="secao in secoes" :key="secao.etapa" cols="12" md="6">
        <div class="rh-revisao pa-4 rounded-lg">
          <div class="d-flex align-center mb-3">
            <v-icon color="primary" class="mr-2">{{ secao.icone }}</v-icon>
            <span class="text-subtitle-1 font-weight-bold primary--text">{{ secao.titulo }}</span>
            <v-spacer />
            <v-btn text small color="primary" @click="$emit('ir-para', secao.etapa)">
              <v-icon left small>mdi-pencil</v-icon> Editar
            </v-btn>
          </div>
          <div
            v-for="campo in secao.campos"
            :key="campo.rotulo"
            class="d-flex justify-space-between rh-revisao__linha py-1"
          >
            <span class="grey--text text-body-2 mr-4">{{ campo.rotulo }}</span>
            <span class="text-body-2 font-weight-medium text-right">{{ campo.valor || "—" }}</span>
          </div>
        </div>
      </v-col>
    </v-row>
  </rh-etapa-card>
</template>

<script>
import RhEtapaCard from "../components/RhEtapaCard.vue";
import { CATEGORIAS_DOCUMENTO, resumoDiasTrabalhados } from "../config/rhConfig";

function descricao(lista, id, campoId = "id", campoTexto = "descricao") {
  const item = (lista || []).find((i) => String(i[campoId]) === String(id));
  return item ? item[campoTexto] : "";
}

function plural(total, singular, pluralTexto) {
  return `${total} ${total === 1 ? singular : pluralTexto}`;
}

export default {
  name: "EtapaRevisao",
  components: { RhEtapaCard },
  props: {
    form: { type: Object, required: true },
    documentos: { type: Array, default: () => [] },
    escalas: { type: Array, default: () => [] },
    lideresList: { type: Array, default: () => [] },
    alterado: { type: Boolean, default: false },
  },
  computed: {
    secoes() {
      const p = this.form.pessoal;
      const profissionais = this.form.profissionais || [];
      const formacoes = (this.form.formacoes || []).filter((f) => f.curso);
      const pendentes = this.documentos.filter((d) => d.novo).length;

      return [
        {
          etapa: 1,
          titulo: "Informações Pessoais",
          icone: "mdi-account-outline",
          campos: [
            { rotulo: "Nome", valor: p.name },
            {
              rotulo: "Estado civil",
              valor: descricao(this.$constants.estadosCivisList, p.estado_civil_id),
            },
            { rotulo: "Sexo", valor: descricao(this.$constants.sexoList, p.sexo_id) },
            { rotulo: "Cor/Raça", valor: p.cor_ou_raca },
            { rotulo: "Nascimento", valor: this.formatarData(p.data_nascimento) },
            { rotulo: "Líder", valor: descricao(this.lideresList, p.lider_id, "id", "name") },
          ],
        },
        {
          etapa: 2,
          titulo: "Documentação e Contato",
          icone: "mdi-card-account-details-outline",
          campos: [
            { rotulo: "CPF", valor: p.cpf },
            { rotulo: "RG", valor: p.rg },
            { rotulo: "CTPS / Série", valor: [p.ctps, p.serie].filter(Boolean).join(" / ") },
            { rotulo: "E-mail", valor: p.email },
            { rotulo: "Telefone", valor: p.telefone },
          ],
        },
        {
          etapa: 3,
          titulo: "Endereço Residencial",
          icone: "mdi-map-marker",
          campos: [
            { rotulo: "CEP", valor: p.cep },
            {
              rotulo: "Logradouro",
              valor: [p.rua_residencia, p.numero_residencia, p.complemento_residencia]
                .filter(Boolean)
                .join(", "),
            },
            { rotulo: "Bairro", valor: p.bairro_residencia },
            {
              rotulo: "Cidade/UF",
              valor: [p.municipio_residencia, p.uf_residencia].filter(Boolean).join(" / "),
            },
          ],
        },
        {
          etapa: 4,
          titulo: "Contratos e Vínculos",
          icone: "mdi-briefcase",
          campos: profissionais.map((prof, i) => ({
            rotulo: `Contrato #${i + 1}`,
            valor: [prof.matricula, prof.cargo_exercicio || prof.cargo_concurso]
              .filter(Boolean)
              .join(" · "),
          })),
        },
        {
          etapa: 5,
          titulo: "Formação Acadêmica",
          icone: "mdi-school",
          campos: [
            { rotulo: "Escolaridade", valor: p.nivel_escolaridade_concluido },
            ...formacoes.map((f, i) => ({ rotulo: `Curso #${i + 1}`, valor: f.curso })),
          ],
        },
        {
          etapa: 6,
          titulo: "Anexos",
          icone: "mdi-file-document-multiple-outline",
          campos: [
            ...CATEGORIAS_DOCUMENTO.map((c) => ({
              rotulo: c.titulo,
              valor: plural(
                this.documentos.filter((d) => d.categoria === c.id).length,
                "arquivo",
                "arquivos"
              ),
            })),
            { rotulo: "Pendentes de envio", valor: plural(pendentes, "arquivo", "arquivos") },
          ],
        },
        {
          etapa: 7,
          titulo: "Escalas de Trabalho",
          icone: "mdi-calendar-clock",
          campos: this.escalas.length
            ? this.escalas.map((e) => ({
                rotulo: `${e.nome}${e.ativa ? "" : " (inativa)"}`,
                valor: resumoDiasTrabalhados(e),
              }))
            : [{ rotulo: "Escalas", valor: "Nenhuma cadastrada" }],
        },
      ];
    },
  },
  methods: {
    formatarData(valor) {
      if (!valor) {
        return "";
      }
      const [ano, mes, dia] = String(valor).substr(0, 10).split("-");
      return dia ? `${dia}/${mes}/${ano}` : valor;
    },
  },
};
</script>

<style scoped>
.rh-revisao {
  background: var(--surface-2);
  border: 1px solid var(--border);
  height: 100%;
}

.rh-revisao__linha + .rh-revisao__linha {
  border-top: 1px solid var(--border-soft);
}
</style>
